import type { Plan, Tenant } from '../types/tenant.js';
import { MODULE_KEYS } from '../types/tenant.js';
import type { TenantUser } from '../types/user.js';

/** Seat usage is the length of the tenant's user list. Never a stored field. */
export function seatsUsed(users: readonly TenantUser[]): number {
  return users.length;
}

/** Number of enabled entitlements. */
export function moduleCount(tenant: Pick<Tenant, 'modules'>): number {
  return MODULE_KEYS.filter((key) => tenant.modules[key]).length;
}

/** Contracted seats by plan. `DATA_MODEL.md` section 2. */
export function seatsByPlan(plan: Plan): number {
  switch (plan) {
    case 'Premier':
      return 50;
    case 'Advanced':
      return 25;
    case 'Essential':
      return 15;
  }
}

/** Support tier by plan. `DATA_MODEL.md` section 2. */
export function supportByPlan(plan: Plan): string {
  switch (plan) {
    case 'Premier':
      return '24x7 with 15 min response';
    case 'Advanced':
      return 'Business hours with 1 h response';
    case 'Essential':
      return 'Business hours with 4 h response';
  }
}

/**
 * Billing label: the MRR when non-zero, else "billing paused" when suspended, else
 * "not billed yet". Formatting of the figure is the caller's job.
 */
export function billingLabel(
  tenant: Pick<Tenant, 'mrr' | 'status'>,
  formatMoney: (n: number) => string,
): string {
  if (tenant.mrr > 0) return formatMoney(tenant.mrr);
  return tenant.status === 'Suspended' ? 'billing paused' : 'not billed yet';
}

export interface UserStatusBreakdown {
  active: number;
  invited: number;
  suspended: number;
  label: string;
}

/** "N active · N invited · N suspended", counted from the list. */
export function userStatusBreakdown(users: readonly TenantUser[]): UserStatusBreakdown {
  const active = users.filter((u) => u.status === 'Active').length;
  const invited = users.filter((u) => u.status === 'Invited').length;
  const suspended = users.filter((u) => u.status === 'Suspended').length;
  return {
    active,
    invited,
    suspended,
    label: `${active} active · ${invited} invited · ${suspended} suspended`,
  };
}

/** Share of contracted seats in use, 0–1. Bars turn warning above 0.85. */
export function seatConsumption(
  tenant: Pick<Tenant, 'seats'>,
  users: readonly TenantUser[],
): number {
  if (tenant.seats <= 0) return 0;
  return Math.min(1, seatsUsed(users) / tenant.seats);
}

/** Suspending sets MRR to 0 and remembers the figure; reactivating restores it. */
export function suspendTenant(tenant: Tenant): Tenant {
  if (tenant.status === 'Suspended') return tenant;
  return { ...tenant, status: 'Suspended', mrr: 0, mrrBeforeSuspension: tenant.mrr };
}

export function reactivateTenant(tenant: Tenant): Tenant {
  if (tenant.status !== 'Suspended') return tenant;
  const { mrrBeforeSuspension, ...rest } = tenant;
  return { ...rest, status: 'Active', mrr: mrrBeforeSuspension ?? 0 };
}
